import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SIGNATURE_DISHES, HERO_IMAGE, KITCHEN_IMAGE, RESTAURANT, HOURS } from "@/data/menu";
import Divider from "@/components/Divider";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Home() {
  const carouselRef = React.useRef(null);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative min-h-[640px] h-[90vh] md:h-screen md:min-h-[680px] w-full overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 kenburns">
          <img
            src={HERO_IMAGE}
            alt="Cinematic Indian cuisine"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1025]/90 via-[#1A1025]/55 to-[#1A1025]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1025]/70 to-transparent" />

        <div className="relative z-10 h-full flex flex-col justify-end pt-28 pb-16 md:pt-0 md:pb-32 px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
            <div className="eyebrow text-[#F5C518] mb-5 md:mb-6" data-testid="hero-eyebrow">
              Coral Springs · Florida
            </div>
          </motion.div>
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-serif text-[#FBF8F3] text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1] md:leading-[0.95] tracking-tight max-w-4xl"
            data-testid="hero-title"
          >
            Authentic Indian Flavors,<br />
            <span className="italic text-[#F5C518]">Crafted</span> with Seven Spices.
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="text-[#FBF8F3]/85 mt-6 md:mt-8 max-w-xl text-sm sm:text-base md:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            From the smoky char of our tandoor to the slow-simmered depth of our curries,
            every dish at 7 Spices is a love letter to India — composed in Coral Springs.
          </motion.p>
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <Link to="/menu" data-testid="hero-cta-menu" className="btn-ghost-light">
              View Menu <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href={RESTAURANT.uberEats}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-order"
              className="btn-ghost-light"
            >
              Order on Uber Eats
            </a>
            <a
              href={RESTAURANT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-directions"
              className="btn-ghost-light"
            >
              Get Directions
            </a>
          </motion.div>
        </div>

        {/* hours strip */}
        <div className="absolute bottom-0 left-0 right-0 z-10 hidden md:block border-t border-[#FBF8F3]/15 bg-[#1A1025]/40 backdrop-blur-md">
          <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between text-[#FBF8F3]/85">
            <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase">
              <Clock className="w-3.5 h-3.5 text-[#F5C518]" />
              Open daily · 11:00 AM – 9:30 PM
            </div>
            <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase">
              <MapPin className="w-3.5 h-3.5 text-[#F5C518]" />
              {RESTAURANT.address}
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32" data-testid="intro-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="eyebrow mb-6">A Coral Springs Tradition</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight text-[#2A1B3D]">
              Seven spices. <br />
              <span className="italic">Endless</span> stories.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 text-[#4A3F55] text-lg leading-relaxed space-y-5"
          >
            <p>
              7 Spices is a celebration of India&apos;s regional kitchens — the smoky tandoors
              of Punjab, the saffron-laced biryanis of Hyderabad, the coconut-rich curries
              of Goa, and the fiery flavors of Andhra.
            </p>
            <p>
              Every dish begins with our signature seven-spice blend, hand-mixed each
              morning, then slow-cooked with care to coax out flavors that feel both
              familiar and brand new.
            </p>
            <div className="pt-3">
              <Link to="/about" data-testid="intro-link-about" className="btn-outline">
                Our Story <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Divider className="text-[#2A1B3D]/40" />

      {/* SIGNATURE CAROUSEL */}
      <section className="py-20 md:py-32 bg-[#FBF8F3]" data-testid="signature-section">
        <div className="px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 md:mb-14">
            <div>
              <div className="eyebrow mb-4">Signature Dishes</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#2A1B3D] max-w-xl">
                A taste of the house.
              </h2>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              <button
                onClick={() => scrollCarousel(-1)}
                data-testid="carousel-prev"
                aria-label="Previous"
                className="w-11 h-11 inline-flex items-center justify-center border border-[#2A1B3D] hover:bg-[#2A1B3D] hover:text-[#FBF8F3] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollCarousel(1)}
                data-testid="carousel-next"
                aria-label="Next"
                className="w-11 h-11 inline-flex items-center justify-center border border-[#2A1B3D] hover:bg-[#2A1B3D] hover:text-[#FBF8F3] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div
            ref={carouselRef}
            data-testid="signature-carousel"
            className="flex gap-5 md:gap-6 overflow-x-auto snap-x-mandatory pb-4 -mx-6 px-6 md:-mx-0 md:px-0"
            style={{ scrollbarWidth: "none" }}
          >
            {SIGNATURE_DISHES.map((d, idx) => (
              <motion.div
                key={d.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.06 }}
                className="snap-start shrink-0 w-[78%] sm:w-[48%] md:w-[38%] lg:w-[28%]"
                data-testid={`signature-card-${idx}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#2A1B3D]/5 group">
                  <img
                    src={d.image}
                    alt={d.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 md:mt-5 flex items-baseline justify-between gap-3">
                  <h3 className="font-serif text-xl md:text-2xl text-[#2A1B3D] leading-tight">{d.name}</h3>
                  <span className="text-[#E91E63] font-bold text-xs md:text-sm tracking-wider whitespace-nowrap">{d.price}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile arrows */}
          <div className="flex sm:hidden items-center justify-center gap-3 mt-6">
            <button
              onClick={() => scrollCarousel(-1)}
              data-testid="carousel-prev-mobile"
              aria-label="Previous"
              className="w-11 h-11 inline-flex items-center justify-center border border-[#2A1B3D]"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollCarousel(1)}
              data-testid="carousel-next-mobile"
              aria-label="Next"
              className="w-11 h-11 inline-flex items-center justify-center border border-[#2A1B3D]"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-10 md:mt-12 text-center">
            <Link to="/menu" data-testid="signature-cta-menu" className="btn-primary">
              View the Full Menu <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOURS & ADDRESS BLOCK */}
      <section
        className="relative py-24 md:py-32 bg-[#2A1B3D] text-[#FBF8F3] overflow-hidden"
        data-testid="visit-section"
      >
        <div className="absolute inset-0 opacity-20">
          <img src={KITCHEN_IMAGE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A1B3D] via-[#2A1B3D]/85 to-[#2A1B3D]/55" />

        <div className="relative px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow text-[#F5C518] mb-6">Plan Your Visit</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              We saved <br />
              <span className="italic text-[#F5C518]">a table</span> for you.
            </h2>
            <p className="mt-8 text-[#FBF8F3]/80 max-w-md leading-relaxed">
              Stop by for dinner, call ahead for takeout, or pick up the family pack on
              your way home. We&apos;re right on Sample Road.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={RESTAURANT.uberEats}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-cta-uber"
                className="btn-ghost-light"
              >
                Order on Uber Eats
              </a>
              <a href={RESTAURANT.phoneHref} data-testid="visit-cta-call" className="btn-ghost-light">
                <Phone className="w-3.5 h-3.5" /> Call {RESTAURANT.phone}
              </a>
              <a
                href={RESTAURANT.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-cta-directions"
                className="btn-ghost-light"
              >
                <MapPin className="w-3.5 h-3.5" /> Get Directions
              </a>
            </div>
          </div>

          <div className="bg-[#FBF8F3]/5 backdrop-blur-sm border border-[#FBF8F3]/10 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-4 h-4 text-[#F5C518]" />
              <span className="text-xs tracking-[0.3em] uppercase font-bold">Hours</span>
            </div>
            <ul className="divide-y divide-[#FBF8F3]/10">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between py-3 text-sm">
                  <span className="font-serif text-lg">{h.day}</span>
                  <span className="text-[#FBF8F3]/75">{h.hours}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-[#FBF8F3]/10 text-sm text-[#FBF8F3]/75 leading-relaxed flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#1ABFB6] mt-0.5" />
              <span>{RESTAURANT.address}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
